// apps/client/src/ui/screens/report.tsx (M2-T08, spec §10.1) — report a chat line, a letter, a resident's name or the town name. The
// server snapshots the content; this panel shows what is being reported and takes an optional note.
import { tuning } from '@whiskerton/content';
import type { ReportKind, TownId } from '@whiskerton/shared';
import { useId, useState, type ReactNode } from 'react';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Panel } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { report: { townId: TownId; kind: ReportKind; refId: string; preview: string } } }

/** The kit's Button with a `data-testid` (the kit Button takes no extra attributes): the e2e suite and the render tests find
 *  the safety actions by these ids. Shared with the other M2-T08 screens (residents, blocked, deleteAccount, admin) together with
 *  `quoteStyle`; it lives here because every file in ui/screens is registered as a screen, and it can go once the kit Button takes
 *  a test id (M0-T03). */
export function SafetyButton(p: { testId?: string; variant?: 'primary' | 'quiet'; disabled?: boolean; onClick?: () => void; children: ReactNode }) {
  return (
    <button type="button" className={`btn btn-${p.variant ?? 'primary'}`} data-testid={p.testId} disabled={p.disabled} onClick={p.onClick}>
      {p.children}
    </button>
  );
}

export const quoteStyle = { margin: 0, padding: '10px 14px', borderRadius: 14, background: 'var(--cream-100)', boxShadow: 'var(--sh-well)',
  fontWeight: 800, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' } as const;

function Report({ params, close }: { params: { townId: TownId; kind: ReportKind; refId: string; preview: string }; close: () => void }) {
  const t = useT();
  const noteId = useId();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  async function send() {
    setBusy(true);
    const ok = await useGame.getState().safetyReport({ townId: params.townId, kind: params.kind, refId: params.refId, note: note.trim() || null });
    setBusy(false);
    if (ok) close();
  }
  return (
    <Panel title={t('safety.reportTitle')}>
      <div style={{ display: 'grid', gap: 12, width: 'min(480px, 72cqw)', maxWidth: '100%' }}>
        <p style={{ margin: 0 }}>{t('safety.reportIntro', { app: t('common.appName') })}</p>
        <blockquote style={quoteStyle}>{params.preview}</blockquote>
        <label className="field" htmlFor={noteId}>
          {t('safety.reportNote')}
          {/* a raw textarea: the kit TextField has no multiline mode */}
          <textarea id={noteId} value={note} rows={3} maxLength={tuning.safety.reportNoteMaxChars} onChange={(e) => setNote(e.target.value)}
            style={{ font: 'inherit', fontWeight: 800, fontSize: 17, lineHeight: 1.45, color: 'var(--wood-800)', background: 'var(--cream-100)', border: 0,
              borderRadius: 14, boxShadow: 'var(--sh-well)', padding: '12px 14px', resize: 'none' }} />
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <SafetyButton testId="report-submit" disabled={busy} onClick={() => void send()}>{t('safety.reportSend')}</SafetyButton>
          <SafetyButton variant="quiet" onClick={close}>{t('common.cancel')}</SafetyButton>
        </div>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'report'> = { id: 'report', layer: 'panel', component: Report };
